import { useState } from "react"
import { Button, DatePicker, Popover } from "antd"
import { Calendar, Check } from "lucide-react"
import dayjs, { type Dayjs } from "dayjs"
import { MAX_WINDOW_DAYS, type DateRange, type RangePreset } from "@csbi/contracts"
import { FILTER_CONTROL_SIZE, FILTER_RANGE_MIN_WIDTH } from "../control-size.js"
import { RANGE_PRESET_LABELS, RANGE_PRESET_ORDER } from "../model/filter-store.js"

export type RangePickerProps = {
  value: DateRange
  onChange: (next: DateRange) => void
  label: string
}

export function RangePicker({ value, onChange, label }: RangePickerProps) {
  const [open, setOpen] = useState(false)
  const activePreset = "preset" in value ? value.preset : null

  const pick = (preset: RangePreset) => {
    onChange({ preset })
    setOpen(false)
  }

  const content = (
    <div style={{ minWidth: FILTER_RANGE_MIN_WIDTH }}>
      {RANGE_PRESET_ORDER.map((preset) => (
        <button
          key={preset}
          type="button"
          onClick={() => pick(preset)}
          aria-pressed={activePreset === preset}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            padding: "6px 10px",
            border: "none",
            borderRadius: 6,
            background: "transparent",
            color: "var(--csbi-ink-primary)",
            fontSize: 13,
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <span style={{ width: 16, display: "inline-flex", justifyContent: "center" }}>
            {activePreset === preset && <Check size={16} strokeWidth={3} />}
          </span>
          {RANGE_PRESET_LABELS[preset]}
        </button>
      ))}

      <div style={{ borderTop: "1px solid var(--csbi-gridline)", marginTop: 6, paddingTop: 8 }}>
        <div style={{ fontSize: 11, color: "var(--csbi-ink-muted)", padding: "0 10px 6px" }}>Custom range</div>
        <div style={{ padding: "0 10px 4px" }}>
          <DatePicker.RangePicker
            size={FILTER_CONTROL_SIZE}
            allowClear={false}
            {...("from" in value
              ? { value: [dayjs(value.from), dayjs(value.to)] as [Dayjs, Dayjs] }
              : {})}
            disabledDate={(d) => d.isAfter(dayjs()) || d.isBefore(dayjs().subtract(MAX_WINDOW_DAYS, "day"))}
            onChange={(dates) => {
              const [from, to] = dates ?? []
              if (!from || !to) return
              onChange({
                from: from.startOf("day").toISOString(),
                to: to.endOf("day").toISOString(),
              })
              setOpen(false)
            }}
          />
        </div>
      </div>
    </div>
  )

  return (
    <Popover open={open} onOpenChange={setOpen} content={content} trigger="click" placement="bottomLeft">
      <Button size={FILTER_CONTROL_SIZE} icon={<Calendar size={14} />}>
        {label}
      </Button>
    </Popover>
  )
}
